/* eslint-disable complexity */
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { LoggerService } from './logger.service';
import { ApiService } from './api.service';
import { AcceptHoldResponse } from '../state/user/holds/models/hold';
import { ErrorDialogFacade } from '../_core/state/dialogs/error/error.facade';
import { ToastService } from '../_core/services/toast.service';
import { AmautasSubscriptionDialogFacade } from '../_core/state/dialogs/amautas-subscription/amautas-subscription.facade';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private readonly toastService = inject(ToastService);
  private readonly errorDialogFacade = inject(ErrorDialogFacade);
  private readonly amautasSubscriptionDialogFacade = inject(AmautasSubscriptionDialogFacade);

  constructor(
    private logger: LoggerService,
    private router: Router,
    private apiService: ApiService,
  ) {}

  handleError(error: HttpErrorResponse, isErrorLoggerServiceActive: boolean) {
    const _error = {
      type: 'error',
      key: 'messages.error.unknown',
      param: undefined,
      suppress: false,
    };

    let redirectHome: boolean;

    if (error?.error?.ERROR === 'ERROR_CHECKOUT_LIMIT_BY_LIBRARY_SURPASSED') {
      _error.key = 'messages.error.tooManyCheckoutsByLibrary';
    } else if (error.url.endsWith('/login')) {
      switch (error.error.errorCode) {
        case 'ERROR_USER_BLOCKED':
          _error.key = 'messages.error.blockedAccount';
          break;
        case 'ERROR_ACCOUNT_PENDING_VERIFY':
          _error.key = 'messages.error.accountPendingVerify';
          break;
        case 'ERROR_USER_END_DATE':
          _error.key = 'messages.error.userExpired';
          break;
        case 'ERROR_LOGIN_UNKNOWN_ID':
        case 'ERROR_LOGIN_UNKNOWN_EMAIL':
          _error.key = 'messages.error.login.wrongCredentials';
          break;
        case 'ERROR_LOGIN_USER_EXPIRED':
          _error.key = 'messages.error.login.user.expired';
          break;
        case 'ERROR_USER_SUSPENDED':
          _error.key = 'messages.error.login.user.suspended';
          break;
        case 'ERROR_LOGIN_USER_NOT_EXIST':
          _error.key = 'messages.error.login.user.not.exist';
          break;
        case 'ERROR_LOGIN_USER_OR_INCORRECT_PASSWORD':
          _error.key = 'messages.error.login.incorrect.user.password';
          break;
        case 'ERROR_LOGIN_EBIBLIO_PIN_NOT_VALID':
          _error.key = 'messages.error.login.ebiblio.pin.not.valid';
          break;
        case 'ERROR_LOGIN_WS_DOWN':
          _error.key = 'messages.error.login.ws.down';
          break;
        case 'ERROR_LOGIN_CHANGE_PASSWORD':
          this.errorDialogFacade.openDialog(error.error.errorInfo.url, 'messages.error.login.change.password');
          break;
        case 'ERROR_LOGIN_CHANGE_EMAIL':
          this.errorDialogFacade.openDialog(error.error.errorInfo.url, 'messages.error.login.change.email');
          break;
        case 'ERROR_LOGIN_CHANGE_DATA':
          this.errorDialogFacade.openDialog(error.error.errorInfo.url, 'messages.error.login.change.data');
          break;
        case 'ERROR_BEACONHOUSE_INVALID_EMAIL':
          _error.key = 'messages.error.beaconhouseInvalidEmail';
          break;
        case 'ERROR_BEACONHOUSE_ERROR':
          _error.key = 'messages.error.beaconhouseError';
          break;
        case 'ERROR_TOO_MANY_LOGIN_ATTEMPTS':
          _error.key = 'messages.error.tooManyLoginAttempts';
          break;
        case 'ERROR_ATTEMPTING_TO_LOGIN_TOO_FREQUENTLY':
          _error.key = 'messages.error.loginTooFrequently';
          break;
        case 'ERROR_ACCOUNT_WAITING_ADMIN':
          _error.key = 'messages.error.waitingAdminValidation';
          break;
        case 'ERROR_LOGIN_INVALID_LIBRARY':
          _error.key = 'messages.error.login.invalidLibrary';
          break;
        case 'ERROR_AMAUTAS_INVALID_STATUS':
          this.amautasSubscriptionDialogFacade.openDialog();
        default:
          _error.key = error.error.errorCode || 'messages.error.unknown';
          break;
      }
    } else if (error.url.includes('/login/remote')) {
      const otkErrorCode = '00' + (error.error - 100); // OTK style, holy cow!
      _error.key = 'messages.error.remoteLogin';
      _error.param = { errorCode: otkErrorCode };
    } else if (error.url.includes('/register')) {
      switch (error.error.errorCode) {
        case 'ERROR_IDENTIFY_EXISTS':
          _error.key = 'messages.error.idExists';
          break;
        case 'ERROR_EMAIL_EXISTS':
          _error.key = 'messages.error.emailExists';
          break;
        case 'ERROR_PROMOCODE':
          _error.key = 'messages.error.codeNotFound';
          break;
        case 'ERROR_PROMOCODE_END_DATE':
          _error.key = 'messages.error.codeEndDate';
          break;
        case 'ERROR_CAF_USER_NOT_FOUND':
          _error.key = 'messages.error.cafUserNotFound';
          break;
        case 'ERROR_CAF_USER_DUPLICATED':
          _error.key = 'messages.error.cafUserDuplicated';
          break;
      }
    } else if (error.url.endsWith('/config')) {
      if (error.status === 401) {
        _error.key = 'messages.error.currentPassword';
      }
    } else if (error.url.includes('/patrons/')) {
      if (error.status === 401) {
        _error.suppress = true;
      }
    } else if (error.url.endsWith('/vote')) {
      if (error.status === 409) {
        switch (error.error) {
          case 'ALREADY_VOTED':
            _error.type = 'info';
            _error.key = 'messages.error.commentRated';
            break;
          case 'VOTE_YOUR_OWN_COMMENT':
            _error.key = 'messages.error.rateOwnComment';
        }
      }
    } else if (error.url.includes('/login/external')) {
      if (error.status === 401) {
        switch (error.error) {
          case 'EZPROXY_ERROR':
          case 'ERROR_INVALID_AFFILIATION_ORGANIZATION':
            _error.key = 'messages.error.invalidAffiliation';
            break;
          case 'ERROR_API_SEED_UNAVAILABLE':
            _error.key = 'messages.error.apiSeedUnavailable';
            break;
        }
      }
      redirectHome = !this.router.url.startsWith('/info/');
    } else if (error.url.endsWith('/forgot')) {
      if (error.status === 400) {
        switch (error.error) {
          case 'RESULT_ERROR_USUARIO_NOT_FOUND':
            _error.key = 'messages.error.userNotFound';
            break;
          case 'RESULT_ERROR_PASSWORD_NOT_CHANGE':
            _error.key = 'messages.error.cantChangePassword';
            break;
        }
      } else if (error.status === 429) {
        _error.key = 'messages.error.tooManyRequests';
      }
    } else if (error.url.endsWith('/checkout') || error.url.includes('/open?annotationSharedId')) {
      if (error.status === 409) {
        switch (error.error.ERROR) {
          case 'TOO_MANY_CHECKOUTS':
          case 'TOO_MANY_CHECKOUTS_BY_USER_POLICY':
            _error.key = 'messages.error.tooManyCheckouts';
            break;
          case 'TOO_MANY_CHECKOUTS_THIS_MONTH':
            _error.key = 'messages.error.tooManyCheckoutsMonth';
            break;
          case 'TOO_MANY_CHECKOUTS_BY_PERIOD_AND_FORMAT':
            _error.key = 'messages.error.tooManyCheckoutsInPeriodForFormat';
            break;
          case 'ERROR_LIMIT_BY_FORMAT_SURPASSED':
            _error.key = 'messages.error.limitByFormat';
            break;
          case 'NOT_ALLOWED_BY_USER_POLICY':
            _error.key = 'messages.error.userPolicyCheckouts';
            redirectHome = true;
            break;
          case 'ERROR_OD_CANNOT_PLACE_CHECKOUT':
            _error.key = 'messages.error.checkout';
            break;
          case 'ERROR_OD_LICENSE_EXPIRED':
            _error.key = 'messages.error.licenseExpired';
            break;
          case 'ERROR_TIME_BETWEEN_LOANS':
            _error.key = 'messages.error.timeBetweenLoans';
            _error.param = error.error.ERRORINFO;
            break;
          case 'ERROR_USER_SUSPENDED':
            _error.key = 'messages.error.suspended';
            break;
          case 'ERROR_NO_FREE_COPIES':
            _error.key = 'messages.error.no.available.copies';
            break;
          case 'ERROR_NO_BALANCE_TO_LOAN':
            _error.key = 'messages.error.noBalanceToLoan';
            break;
        }
      } else if (error.status === 412) {
        if (error.error.errorCode === 'ERROR_GEOLOCATION') {
          _error.key = 'general.invalidLocation';
          _error.type = 'info';
        }
      }
    } else if (error.url.endsWith('/hold')) {
      switch (error.error.ERROR) {
        case 'HOLD_TOO_MANY_EXCEPTION':
        case 'ERROR_USER_HAS_TOO_MANY_HOLDS':
          _error.key = 'messages.error.tooManyHolds';
          break;
        case 'ERROR_RESOURCE_HAS_TOO_MANY_HOLDS':
          _error.key = 'messages.error.tooManyHoldsForRecord';
      }
    } else if (new RegExp('/records/[0-9]+/download').test(error.url)) {
      if (error.status === 404) {
        _error.key = 'messages.error.fileNotFound';
      } else {
        _error.key = 'messages.error.downloadGeneric';
      }
    } else if (error.url.includes('/records')) {
      if (error.status === 404) {
        _error.suppress = true;
        redirectHome = true;
      } else if (error.status === 409) {
        switch (error.error.ERROR) {
          case 'NOT_ALLOWED_BY_USER_POLICY':
            _error.key = 'messages.error.userPolicyView';
            redirectHome = true;
            break;
          case 'ERROR_DISCHARGED':
            _error.suppress = true;
            break;
        }
      }
    } else if (error.url.includes('/lists/')) {
      if (error.status === 404) {
        const listId = error.url.split('/');
        _error.suppress = true;
        this.logger.error("List doesn't exist", listId.pop());
      }
    } else if (new RegExp('/checkouts/[0-9]+/download').test(error.url)) {
      if (error.status === 410) {
        _error.key = 'messages.error.resourceNotAvailable';
      }
    } else if (new RegExp('/checkouts/[0-9]+/return').test(error.url)) {
      if (error.status === 500) {
        _error.key = 'messages.error.returnCheckout';
      }
    } else if (error.url.includes('/ratings')) {
      _error.suppress = true;
    } else if (error.url.includes('/publisher')) {
      _error.suppress = true;
    } else if (error.url.includes('/code/validate')) {
      switch (error.error) {
        case 'ERROR_PROMOCODE_END_DATE':
          _error.key = 'messages.error.codeEndDate';
          break;
        default:
          _error.key = 'messages.error.codeNotFound';
      }
    } else if (error.url.includes('/holds/accept')) {
      redirectHome = true;
      const response = error.error as AcceptHoldResponse;

      switch (response.status) {
        case 'BAD_REQUEST':
        case 'MORE_THAN_ONE_HOLD':
        case 'UNKNOWN_USER':
        case 'HOLD_DOES_NOT_EXIST':
          _error.suppress = true;
          break;
        case 'DOES_NOT_BELONG_TO_USER':
          _error.key = 'messages.holds.accept.wrongUser';
          break;
        case 'EXPIRED_HOLD':
          _error.key = 'messages.holds.accept.expired';
          break;
      }
    } else {
      _error.suppress = true;
      this.logger.error(error);

      if (isErrorLoggerServiceActive) {
        const newHttpRequestError = {
          type: 'http',
          message: error.message,
          httpRequest: {
            url: error.url,
            body: error.message,
            headers: error.headers,
            httpStatusCode: error.status,
          },
        };

        this.apiService.logNewUnhandledError(newHttpRequestError).subscribe();
      }
    }

    if (_error.key === 'messages.error.unknown') {
      _error.suppress = true;
    }

    if (!_error.suppress) {
      this.toastService.showToast(_error.key, _error.type as 'error', _error.param);
    }

    if (redirectHome) {
      this.router.navigate(['/']);
    }
  }
}
